import * as React from 'react'
import { X } from 'lucide-react'
import { cn } from 'shared/lib/utils'
import { Input } from 'shared/components/ui/input'
import { useTranslate } from 'shared/lib/i18n'

/** Multi-file variant of ImageDropzone: thumbnail grid + add tile. Order = add order; first image is the cover. */
export function MultiImageDropzone({
	urls,
	onFiles,
	onRemove,
	uploadingCount = 0,
	disabled,
	max = 10,
}: {
	urls: string[]
	onFiles: (files: File[]) => void
	onRemove: (index: number) => void
	uploadingCount?: number
	disabled?: boolean
	max?: number
}) {
	const { t, prefixT } = useTranslate('pages.imageUpload')
	const inputRef = React.useRef<HTMLInputElement>(null)
	const [dragOver, setDragOver] = React.useState(false)
	const remaining = max - urls.length - uploadingCount
	const canAdd = !disabled && remaining > 0

	function pick(files: FileList | null) {
		if (!files) return
		const images = [...files]
			.filter((f) => f.type.startsWith('image/'))
			.slice(0, Math.max(0, remaining))
		if (images.length > 0) onFiles(images)
	}

	return (
		<div className="space-y-2">
			<div className="grid grid-cols-3 gap-2">
				{urls.map((url, i) => (
					<div key={url} className="relative">
						<img
							src={url}
							alt={prefixT('photoAlt', { index: String(i + 1) })}
							className="aspect-square w-full rounded-md object-cover"
						/>
						{i === 0 && (
							<span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
								{prefixT('cover')}
							</span>
						)}
						<button
							type="button"
							aria-label={prefixT('removePhoto', { index: String(i + 1) })}
							disabled={disabled}
							onClick={() => onRemove(i)}
							className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-black/60 text-white"
						>
							<X className="size-3.5" />
						</button>
					</div>
				))}
				{Array.from({ length: uploadingCount }, (_, i) => (
					<div
						key={`uploading-${i}`}
						className="grid aspect-square animate-pulse place-items-center rounded-md border border-dashed border-input text-xs text-muted-foreground"
					>
						{t('common.loading')}
					</div>
				))}
			</div>
			{canAdd && (
				<div
					role="button"
					tabIndex={0}
					onClick={() => inputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							inputRef.current?.click()
						}
					}}
					onDragOver={(e) => {
						e.preventDefault()
						setDragOver(true)
					}}
					onDragLeave={() => setDragOver(false)}
					onDrop={(e) => {
						e.preventDefault()
						setDragOver(false)
						pick(e.dataTransfer.files)
					}}
					className={cn(
						'flex min-h-20 cursor-pointer items-center justify-center rounded-lg border border-dashed border-input p-3 text-center text-sm text-muted-foreground transition-colors',
						dragOver && 'border-ring bg-accent',
					)}
				>
					{urls.length === 0
						? prefixT('dropzone', { max: String(max) })
						: prefixT('addMore', { remaining: String(remaining) })}
				</div>
			)}
			<Input
				ref={inputRef}
				type="file"
				accept="image/*"
				multiple
				className="hidden"
				disabled={disabled}
				onChange={(e) => {
					pick(e.target.files)
					e.target.value = ''
				}}
			/>
		</div>
	)
}
